// Remove the direct fabric import since it's a global variable
// import { fabric } from 'fabric';

// Simple in-memory cache for previews
const previewCache = new Map();

// Queue for throttling preview generation
const previewQueue = [];
let isProcessingQueue = false;

/**
 * Generates an optimized SVG preview for a canvas state
 * @param {Array} canvasState - The fabric.js canvas state objects
 * @param {Object} options - Optional configuration
 * @param {boolean} options.useCache - Whether to use caching (default: true)
 * @param {number} options.scaleFactor - Scale factor for the preview (default: 0.5)
 * @returns {Promise<string>} - Promise resolving to the SVG string
 */
export function generatePreviewForSlide(canvasState, options = {}) {
  const {
    useCache = true,
    scaleFactor = 0.5
  } = options;
  
  // Generate a cache key based on the canvasState
  const cacheKey = useCache ? JSON.stringify(canvasState) : null;
  
  // Check if we have a cached version
  if (useCache && cacheKey && previewCache.has(cacheKey)) {
    return Promise.resolve(previewCache.get(cacheKey));
  }
  
  return new Promise((resolve, reject) => {
    try {
      // Early return for empty canvasState
      if (!canvasState || !Array.isArray(canvasState) || canvasState.length === 0) {
        return resolve('');
      }
      
      // Get fabric from window since it's loaded globally
      const fabric = window.fabric;
      if (!fabric) {
        throw new Error('Fabric.js not loaded');
      }

      // Create a temporary canvas with timeout protection
      let timeoutId = setTimeout(() => {
        // Clean up any partial resources
        if (tempCanvas) tempCanvas.dispose();
        if (previewCanvas) previewCanvas.dispose();
        reject(new Error('Preview generation timed out'));
      }, 10000); // 10 second timeout
      
      let tempCanvas = null;
      let previewCanvas = null;
      
      try {
        // Create a temporary canvas
        tempCanvas = new fabric.Canvas(null, {
          width: 375,
          height: 667
        });
        
        // Load the canvas state
        tempCanvas.loadFromJSON({ objects: canvasState }, () => {
          try {
            // Create another temp canvas for the scaled version
            previewCanvas = new fabric.Canvas(null, {
              width: 375 * scaleFactor,
              height: 667 * scaleFactor
            });
            
            // Clone and scale all objects
            tempCanvas.getObjects().forEach(obj => {
              try {
                const clonedObj = fabric.util.object.clone(obj);
                clonedObj.scaleX = obj.scaleX * scaleFactor;
                clonedObj.scaleY = obj.scaleY * scaleFactor;
                clonedObj.left = obj.left * scaleFactor;
                clonedObj.top = obj.top * scaleFactor;
                previewCanvas.add(clonedObj);
              } catch (objError) {
                console.warn('Error processing object during preview generation:', objError);
                // Continue with other objects even if one fails
              }
            });
            
            // Generate optimized SVG
            const svgData = previewCanvas.toSVG({
              viewBox: {
                x: 0,
                y: 0,
                width: 375 * scaleFactor,
                height: 667 * scaleFactor
              },
              width: 375 * scaleFactor,
              height: 667 * scaleFactor,
              suppressPreamble: false,
              preserveAspectRatio: 'xMidYMid meet'
            });
            
            // Optimize SVG
            const optimizedSvg = optimizeSvg(svgData, scaleFactor);
            
            // Cache the result if caching is enabled
            if (useCache && cacheKey) {
              previewCache.set(cacheKey, optimizedSvg);
              
              // Limit cache size to prevent memory leaks
              if (previewCache.size > 100) {
                // Remove oldest entry (first key)
                const firstKey = previewCache.keys().next().value;
                previewCache.delete(firstKey);
              }
            }
            
            // Clear timeout since we're done
            clearTimeout(timeoutId);
            
            // Clean up
            tempCanvas.dispose();
            previewCanvas.dispose();
            
            resolve(optimizedSvg);
          } catch (innerError) {
            clearTimeout(timeoutId);
            console.error('Error generating SVG:', innerError);
            
            // Clean up
            if (tempCanvas) tempCanvas.dispose();
            if (previewCanvas) previewCanvas.dispose();
            
            reject(innerError);
          }
        });
      } catch (canvasError) {
        clearTimeout(timeoutId);
        console.error('Error creating canvas:', canvasError);
        
        // Clean up
        if (tempCanvas) tempCanvas.dispose();
        if (previewCanvas) previewCanvas.dispose();
        
        reject(canvasError);
      }
    } catch (err) {
      console.error('Error in preview generation:', err);
      reject(err);
    }
  });
}

/**
 * Throttled version of generatePreviewForSlide that queues requests and processes them
 * one at a time to avoid overwhelming the browser
 * 
 * @param {Array} canvasState - The fabric.js canvas state objects
 * @param {Object} options - Optional configuration
 * @param {Function} options.onStart - Callback when generation starts
 * @param {Function} options.onComplete - Callback when generation completes
 * @returns {Promise<string>} - Promise resolving to the SVG string
 */
export function throttledGeneratePreview(canvasState, options = {}) {
  const { 
    onStart,
    onComplete,
    ...genOptions
  } = options;
  
  return new Promise((resolve, reject) => {
    // Add to queue
    previewQueue.push({
      canvasState,
      options: genOptions,
      resolve: (result) => {
        if (onComplete) onComplete(result);
        resolve(result);
      },
      reject: (error) => {
        if (onComplete) onComplete(null, error);
        reject(error);
      },
      onStart
    });
    
    // Start processing queue if not already processing
    if (!isProcessingQueue) {
      processNextInQueue();
    }
  });
}

/**
 * Processes the next item in the preview generation queue
 */
function processNextInQueue() {
  if (previewQueue.length === 0) {
    isProcessingQueue = false;
    return;
  }
  
  isProcessingQueue = true;
  const nextItem = previewQueue.shift();
  
  // Signal that processing has started for this item
  if (nextItem.onStart) {
    nextItem.onStart();
  }
  
  generatePreviewForSlide(nextItem.canvasState, nextItem.options)
    .then(result => {
      nextItem.resolve(result);
      
      // Add a small delay to allow browser to breathe between operations
      setTimeout(() => {
        processNextInQueue();
      }, 50);
    })
    .catch(error => {
      nextItem.reject(error);
      
      // Continue processing queue even if one item fails
      setTimeout(() => {
        processNextInQueue();
      }, 50);
    });
}

/**
 * Batch generates previews for multiple slides with throttling to avoid performance issues
 * 
 * @param {Array<Object>} slides - Array of slide objects with canvasState
 * @param {Object} options - Optional configuration
 * @param {Function} options.onProgress - Callback with progress updates (slideIndex, status)
 * @returns {Promise<Array>} - Promise resolving to array of preview results
 */
export function batchGeneratePreviews(slides, options = {}) {
  const { onProgress, ...genOptions } = options;
  
  return new Promise((resolve, reject) => {
    if (!slides || !Array.isArray(slides) || slides.length === 0) {
      return resolve([]);
    }
    
    const results = new Array(slides.length);
    let completedCount = 0;
    let hasError = false;
    
    slides.forEach((slide, index) => {
      throttledGeneratePreview(
        slide.canvasState, 
        { 
          ...genOptions,
          onStart: () => {
            if (onProgress) onProgress(index, 'started');
          },
          onComplete: (result, error) => {
            if (onProgress) onProgress(index, error ? 'error' : 'completed');
          }
        }
      )
        .then(preview => {
          results[index] = { index, preview, success: true };
          completedCount++;
          
          if (completedCount === slides.length) {
            resolve(results);
          }
        })
        .catch(error => {
          console.error(`Error generating preview for slide ${index}:`, error);
          results[index] = { index, error, success: false };
          completedCount++;
          hasError = true;
          
          if (completedCount === slides.length) {
            if (hasError) {
              // Still resolve but with a partial result
              resolve(results.filter(r => r.success));
            } else {
              resolve(results);
            }
          }
        });
    });
  });
}

/**
 * Clears the preview cache
 */
export function clearPreviewCache() {
  previewCache.clear();
}

/**
 * Optimizes an SVG string for performance and size
 * @param {string} svgData - The raw SVG data
 * @param {number} scaleFactor - The scale factor used for the preview
 * @returns {string} The optimized SVG
 */
function optimizeSvg(svgData, scaleFactor) {
  if (!svgData) return '';
  
  try {
    // Apply whitespace and redundant character optimizations
    return svgData
      .replace(/\s+/g, ' ')             // Replace multiple whitespace with single space
      .replace(/>\s+</g, '><')          // Remove whitespace between tags
      .replace(/\s+>/g, '>')            // Remove whitespace before closing angle bracket
      .replace(/\s+\/>/g, '/>')         // Remove whitespace in self-closing tags
      .replace(/<svg/, `<svg width="${375 * scaleFactor}" height="${667 * scaleFactor}"`) // Add explicit dimensions
      .replace(/<!--.*?-->/g, '')       // Remove comments
      .replace(/\s*stroke="none"\s*/g, ' ') // Remove stroke="none" attributes
      .replace(/\s*fill-opacity="[1]"\s*/g, ' '); // Remove fill-opacity="1" attributes
  } catch (error) {
    console.warn('Error optimizing SVG:', error);
    return svgData; // Return original if optimization fails
  }
} 